import { useCallback } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useShallow } from 'zustand/shallow';

import { WallFormingBaseRoute } from 'app/modules/routePaths';
import { AUTO_FORM_SEARCH_PARAM } from 'app/modules/wallforming/components/auto-form/AutoFormOnSessionLoad';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

/**
 * Mirrors a project load: clears workspace state, detaches the active project and switches to the session —
 * in place inside the forming workspace, or by navigating there from other pages.
 */
export const useOpenFormingSession = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const {
        setArticles,
        setArticleInstanceSelections,
        setPois,
        setActiveProject,
        setLastSavedSignature,
        setSelectedStoreyGuid
    } = usePlacementStore(
        useShallow((state) => ({
            setArticles: state.setArticles,
            setArticleInstanceSelections: state.setArticleInstanceSelections,
            setPois: state.setPois,
            setActiveProject: state.setActiveProject,
            setLastSavedSignature: state.setLastSavedSignature,
            setSelectedStoreyGuid: state.setSelectedStoreyGuid
        }))
    );

    return useCallback(
        (sessionId: string, autoForm = false) => {
            setArticles({});
            setArticleInstanceSelections({});
            setPois([]);
            setActiveProject(null);
            setLastSavedSignature('');
            setSelectedStoreyGuid(null);

            const isInWorkspace = pathname.startsWith(`/${WallFormingBaseRoute}`);
            const nextParams = new URLSearchParams(isInWorkspace ? searchParams : undefined);
            nextParams.set('formingsession', sessionId);
            nextParams.delete('projectId');
            if (autoForm) {
                nextParams.set(AUTO_FORM_SEARCH_PARAM, '1');
            } else {
                nextParams.delete(AUTO_FORM_SEARCH_PARAM);
            }
            if (isInWorkspace) {
                setSearchParams(nextParams, { replace: true });
            } else {
                void navigate(`/${WallFormingBaseRoute}?${nextParams.toString()}`);
            }
        },
        [
            navigate,
            pathname,
            searchParams,
            setActiveProject,
            setArticleInstanceSelections,
            setArticles,
            setLastSavedSignature,
            setPois,
            setSearchParams,
            setSelectedStoreyGuid
        ]
    );
};
